import { useSelectedDelivery } from '../../features/sportsnewDelivery/hooks/selectedDelivery.hook.js';
import { useRealtimeDelivery } from '../../features/sportsnewDelivery/hooks/realtimeDelivery.hook.js';
import { isSportsnewFixturePatchForActiveDetail, sportsnewActiveFixtureMode, sportsnewFixtureRoomForIdentity, sportsnewFixtureMetadataDelta, sportsnewFixturePatchTargetId } from './sportsnewFixturePatchEnvelope';
import { getSportsnewBackUrl } from './sportsnewRuntimeConfig';
import { normalizeSelectedRecoveryIds } from './sportsnewSelectedRecovery';
import { normalizeSportsnewCategories, normalizeSportsnewComboBonus } from './sportsnewContextConfig';
import { useSportsnewListRealtimePatch } from '../_hooks/useSportsnewListRealtimePatch';
import { useSportsnewSelectedRealtime } from '../_hooks/useSportsnewSelectedRealtime';
import { acceptSportsnewDetailRealtimeOdd } from './sportsnewDetailRealtime';
import { mapSportsnewFixtureOdds, mapSportsnewMarketOdds, mapSportsnewListPatchOdds, failCloseSportsnewDetailMarkets } from './sportsnewListPatch';
import {
  sportsnewMembershipPatchRequiresFailClosedHold,
  sportsnewMembershipPatchProvidesReopenAuthority,
} from './sportsnewMembershipRevision';
import { useCallback, useEffect, useRef } from 'react';
import io from 'socket.io-client';

import { applyFixturePatchToFixture, applyFixturePatchToFixtureList, diffMainOdds, fxId } from './sportsnewListPatch';
import {
  normalizeSportsnewFixturePatchContract,
  sportsnewFixturePatchId,
  sportsnewFixturePatchMatchesFixture,
} from './sportsnewFixturePatchContract';
import { applyFixtureDetailPatch, isExplicitEmptyFullMarketProjection } from './sportsnewDetailPatch';
import { recordSportsnewDetailRealtimeMarkets, sportsnewRealtimeMarketsFromFixturePatch, sportsnewRealtimeOddsFromFixturePatch } from './sportsnewDetailRealtime';

import { sportsnewOddStateIdOf } from './sportsnewOddStateId';

import { resolveOperatorVisibilityPatch } from './sportsnewOperatorVisibility';
import { sportsnewFixturePatchHasMembershipTransition } from './sportsnewMembershipRevision';
import {
  SPORTSNEW_MEMBERSHIP_INVALIDATE_EVENT,
} from './sportsnewMembershipInvalidation';
import { SPORTSNEW_WS_DEBUG } from './sportsnewContextConfig';
import { markWsPatchReceived, measureWsPatchToRender } from './sportsnewPerf';

export const useSportsnewRealtime = ({
  frameDeliveryRef, projectionObserverRef, listReady, deliveryDetailGenerationRef,
  SportsnewService,
  interruptListRequestForSocket, resumeInterruptedListRequest,
  detailFixtureId, mode, selections, socketConnected, heartbeatRef, heartbeatListWithGenerationFence,
  setCategories, setComboBonus, setComboBuckets, setDisplaySettings,
  acceptListSnapshot, listPresentationRef, listStreamRef, requestPresentationListRecovery,
  armMembershipQuarantineReopen,
  cancelPendingDetailWork,
  failCloseCartSelectionsForFixtures,
  failCloseQuarantinedFixtures,
  fixturesRequestSeqRef,
  listFilterKeyRef,
  listHeartbeatFenceRef,
  listSubscriptionRecoveryRef,
  membershipFailClosedFixturesRef,
  quarantineMembershipFixtures,
  recoverFixtureDetailSingleFlight,
  scheduleMembershipGenerationRefresh,
  sessionOwnerRef,
  socketGenerationRef,
  fixtureRecoveryInFlightRef,
  displayScope,
  clearActiveDetailIfSuspended,
  detailCacheRef,
  detailFixtureIdRef,
  detailMarketsRef,
  detailRealtimeLedgerRef,
  displayScopeRef,
  fixtureSequenceRef,
  fixturesRef,
  isSelectionInAnyCart,
  listRecoveryInFlightRef,
  listRecoveryLastRef,
  listSequenceRef,
  listSubscriptionRef,
  modeRef,
  onMembershipInvalidation,
  selectedRecoveryInFlightRef,
  selectedSubscriptionRef,
  selectedSequenceRef,
  selectedWsFreshnessRef,
  selectionsRef,
  sessionOwner,
  setDetailFixture,
  setDetailMarkets,
  setDetailLoading,
  setFixtures,
  setOddsFlash,
  setSocketConnected,
  socketRef,
  subscribeListWithGenerationFence,
  updateOddsInCart,
  updateOperatorSuspendedFixtureIds,
}) => {

  const selectedAuthoritativeRecoveryOrdinalRef = useRef(0);
  const fixtureSubscriptionRef = useRef(null);
  const fixtureHeartbeatRef = useRef(null);
  const selectedHeartbeatRef = useRef(null);
  /* ─────────────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────────────── */

  const FLASH_DURATION_MS = 2000;

  const oddsFlashTimersRef = useRef(new Map());

  const flashOdds = useCallback((oddStateId, direction) => {
    const key = String(oddStateId);
    const timers = oddsFlashTimersRef.current;
    if (timers.has(key)) clearTimeout(timers.get(key));
    setOddsFlash((prev) => (
      prev[key] === direction ? prev : { ...prev, [key]: direction }
    ));
    const timer = setTimeout(() => {
      // A cancelled callback may already be queued. Only the current owner
      // may expire this ID, including after unmount/StrictMode cleanup.
      if (timers.get(key) !== timer) return;
      timers.delete(key);
      setOddsFlash((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, key)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, FLASH_DURATION_MS);
    timers.set(key, timer);
  }, [setOddsFlash]);
  useEffect(() => () => {
    oddsFlashTimersRef.current.forEach(clearTimeout);
    oddsFlashTimersRef.current.clear();
  }, []);

  const commitDetailMarkets = setDetailMarkets;
  const commitFixtures = setFixtures;

  const sendAck = useCallback((room, sequence, emittedAt) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit('ack:sportsnew:patch', { room, sequence, emittedAt });
  }, [socketRef]);

  // Compare a fixture's old/new mainOdds (home/draw/away) and flash whichever
  // slots actually changed price. Shared by both list-patch shapes (field-patch
  // and ops-upsert) so the diff logic lives in exactly one place.
  const flashMainOddsDiff = useCallback((oldMainOdds, newMainOdds) => {
    diffMainOdds(oldMainOdds, newMainOdds).forEach(({ oddStateId, direction }) => flashOdds(oddStateId, direction));
  }, [flashOdds]);

  const updateDetailCacheForFixtures = useCallback((fixtureIds, updateMarkets) => {
    const prefixes = new Set((fixtureIds ?? []).map((id) => `${String(id)}:`));
    if (prefixes.size === 0 || typeof updateMarkets !== 'function') return;
    detailCacheRef.current.forEach((cached, cacheKey) => {
      const prefix = [...prefixes].find((candidate) => cacheKey.startsWith(candidate));
      if (!prefix) return;
      const nextMarkets = updateMarkets(cached?.markets, prefix.slice(0, -1));
      if (nextMarkets === null) detailCacheRef.current.delete(cacheKey);
      else if (nextMarkets !== cached?.markets) {
        detailCacheRef.current.set(cacheKey, { ...cached, markets: nextMarkets });
      }
    });
  }, [detailCacheRef]);

  /* One bounded recovery request per fixture at a time. Reconnect, sequence
     gap, and unplaceable-new-odd signals can overlap; coalescing them here
     prevents duplicate authoritative REST snapshots. */


  /* ─────────────────────────────────────────────────────
     SOCKET EVENT HANDLERS
  ───────────────────────────────────────────────────── */

  const acceptDisplayPatch = useCallback((patch, kind, order = null, { presentationOrderConfirmed = false } = {}) => {
    const currentById = new Map();
    const fixtureByOddId = new Map();
    const recoverIds = new Map();
    const remember = odd => {
      const id = odd?.oddStateId ?? odd?.id;
      if (id != null) currentById.set(String(id), odd);
      return odd;
    };
    selectionsRef.current.forEach(selection => {
      const id = sportsnewOddStateIdOf(selection);
      if(id == null)return;
      const fixtureId = selection.match_id ?? selection.matchId ?? selection.fixtureId ?? selection.fixture_id;
      if(fixtureId != null)fixtureByOddId.set(String(id),String(fixtureId));
      remember({...selection,oddStateId:id,priceDecimal:selection.odds,
        bettable:selection.status !== 'locked',locked:selection.status === 'locked'});
    });
    fixturesRef.current.forEach(fixture => mapSportsnewFixtureOdds(fixture, odd => {
      fixtureByOddId.set(String(odd?.oddStateId ?? odd?.id), fxId(fixture));
      return remember(odd);
    }));
    mapSportsnewMarketOdds(detailMarketsRef.current, odd => {
      fixtureByOddId.set(String(odd?.oddStateId ?? odd?.id), detailFixtureIdRef.current);
      return remember(odd);
    });
    const accept = odd => acceptSportsnewDetailRealtimeOdd(
      detailRealtimeLedgerRef.current, odd, currentById.get(String(odd?.oddStateId ?? odd?.id)), order,
      id => { const fixtureId = fixtureByOddId.get(id); if (fixtureId != null) {
        if(!recoverIds.has(fixtureId))recoverIds.set(fixtureId,[]);
        if(recoverIds.get(fixtureId).length<50)recoverIds.get(fixtureId).push(id);
      } },
      { requireWriterForUnlock: modeRef.current === 'live',
        presentationOrderConfirmed: kind === 'list' && presentationOrderConfirmed },
    );
    let result;
    if (kind === 'list') result = mapSportsnewListPatchOdds(patch, accept);
    else if (kind === 'fixture') {
      const mapped = mapSportsnewFixtureOdds(patch, accept);
      result = mapped?.oddStateId != null ? accept(mapped) : mapped;
    } else result = accept(patch);
    recoverIds.forEach((ids,fixtureId) => recoverFixtureDetailSingleFlight(fixtureId, { snapshot: true,focusOddStateIds:ids }));
    return result;
  }, [
    detailFixtureIdRef, detailMarketsRef, detailRealtimeLedgerRef, fixturesRef, modeRef,
    recoverFixtureDetailSingleFlight, selectionsRef,
  ]);

  const handleListPatch = useSportsnewListRealtimePatch({
    SportsnewService,
    frameDeliveryRef,
    acceptDisplayPatch,
    acceptListSnapshot, listPresentationRef, listStreamRef, requestPresentationListRecovery,
    armMembershipQuarantineReopen,
    cancelPendingDetailWork,
    clearActiveDetailIfSuspended,
    commitDetailMarkets,
    commitFixtures,
    detailFixtureIdRef,
    detailRealtimeLedgerRef,
    failCloseQuarantinedFixtures,
    fixturesRef,
    fixturesRequestSeqRef,
    flashMainOddsDiff,
    listFilterKeyRef,
    listRecoveryInFlightRef,
    listRecoveryLastRef,
    listSequenceRef,
    listSubscriptionRef,
    membershipFailClosedFixturesRef,
    modeRef,
    quarantineMembershipFixtures,
    recoverFixtureDetailSingleFlight,
    scheduleMembershipGenerationRefresh,
    sendAck,
    sessionOwnerRef,
    socketGenerationRef,
    updateDetailCacheForFixtures,
    updateOperatorSuspendedFixtureIds,
  });

  const handleFixturePatch = useCallback(({ sequence, emittedAt, patch }) => {
    markWsPatchReceived('fixture', sequence);
    let normalizedPatch = normalizeSportsnewFixturePatchContract(patch);
    const patchFixtureId = sportsnewFixturePatchId(normalizedPatch);
    const activeFixtureId = detailFixtureIdRef.current;
    // Unsubscribe and room-switch are asynchronous. A final patch from the
    // previous fixture can therefore arrive after the next card was selected.
    // Fixture patches are detail-scoped, so identity mismatch (including a
    // missing identity) must fail closed before touching ledger/cache/state.
    if (!sportsnewFixturePatchMatchesFixture(normalizedPatch, activeFixtureId)) {
      sendAck(
        normalizedPatch?.room || `sportsnew:fixture:${patchFixtureId ?? 'stale'}`,
        sequence,
        emittedAt,
      );
      measureWsPatchToRender('fixture', sequence);
      return;
    }
    const activeDetailIdentity = {
      fixtureId: activeFixtureId,
      mode: sportsnewActiveFixtureMode(modeRef.current),
      scope: displayScopeRef.current,
    };
    if (!isSportsnewFixturePatchForActiveDetail(normalizedPatch, activeDetailIdentity)) {
      const staleFixtureId = sportsnewFixturePatchTargetId(normalizedPatch);
      sendAck(normalizedPatch?.room || `sportsnew:fixture:${staleFixtureId ?? 'unknown'}`, sequence, emittedAt);
      measureWsPatchToRender('fixture', sequence);
      return;
    }
    const realtimeRevisionBeforePatch = detailRealtimeLedgerRef.current.revision;
    if (Number.isSafeInteger(sequence) && fixtureSequenceRef.current !== null
      && sequence <= fixtureSequenceRef.current) {
      sendAck(normalizedPatch?.room || `sportsnew:fixture:${patchFixtureId}`, sequence, emittedAt);
      measureWsPatchToRender('fixture', sequence);
      return;
    }
    const authoritativeEmpty = isExplicitEmptyFullMarketProjection(normalizedPatch);
    if (authoritativeEmpty) {
      // A full empty snapshot excludes contradictory odds before display admission.
      normalizedPatch = { ...normalizedPatch, odds: [], oddStateId: null };
    }
    normalizedPatch = acceptDisplayPatch(normalizedPatch, 'fixture', {
      room: `fixture:${patchFixtureId}`, connection: socketGenerationRef.current, sequence,
    });
    if (authoritativeEmpty) {
      // An accepted full replacement supersedes pending detail GET/RAF and
      // selected-detail recovery, while preserving this selection and any
      // still-current list/cart consumers of a shared recovery request.
      cancelPendingDetailWork({ preserveActiveDetail: true });
      setDetailLoading(false);
      const cacheKey = `${activeFixtureId}:${modeRef.current}:${displayScopeRef.current}`;
      const cached = detailCacheRef.current.get(cacheKey);
      if (cached) detailCacheRef.current.set(cacheKey, { ...cached, markets: [] });
    }
    const realtimeFixtureOdds = sportsnewRealtimeOddsFromFixturePatch(normalizedPatch);
    recordSportsnewDetailRealtimeMarkets(
      detailRealtimeLedgerRef.current,
      sportsnewRealtimeMarketsFromFixturePatch(normalizedPatch),
      realtimeFixtureOdds,
    );
    if (!authoritativeEmpty && detailFixtureIdRef.current != null) {
      updateDetailCacheForFixtures(
        [detailFixtureIdRef.current],
        (markets) => {
          const cachedPatch = applyFixtureDetailPatch(markets, normalizedPatch);
          if (cachedPatch.needsRecovery) return null;
          return membershipFailClosedFixturesRef.current.has(String(detailFixtureIdRef.current))
            ? failCloseSportsnewDetailMarkets(cachedPatch.markets)
            : cachedPatch.markets;
        },
      );
    }
    const operatorVisibility = resolveOperatorVisibilityPatch(normalizedPatch);
    const fixtureMembershipTransition = sportsnewFixturePatchHasMembershipTransition({
      fixtures: fixturesRef.current,
      patch: normalizedPatch,
      fallbackFixtureId: detailFixtureIdRef.current,
      mode: modeRef.current,
    });
    const failClosedFixtureHold = fixtureMembershipTransition
      && sportsnewMembershipPatchRequiresFailClosedHold(normalizedPatch);
    if (failClosedFixtureHold) {
      quarantineMembershipFixtures([patchFixtureId ?? detailFixtureIdRef.current]);
    }
    if (
      !failClosedFixtureHold
      && (
        operatorVisibility.action === 'resume'
        || sportsnewMembershipPatchProvidesReopenAuthority(normalizedPatch)
      )
    ) {
      armMembershipQuarantineReopen([
        patchFixtureId ?? detailFixtureIdRef.current,
        ...operatorVisibility.fixtureIds,
      ]);
    }
    if (operatorVisibility.action !== null || fixtureMembershipTransition) {
      scheduleMembershipGenerationRefresh();
    }
    if (operatorVisibility.action === 'suspend') {
      fixtureSequenceRef.current = sequence;
      updateOperatorSuspendedFixtureIds(operatorVisibility.fixtureIds, true);
      clearActiveDetailIfSuspended(operatorVisibility.fixtureIds);
      sendAck(normalizedPatch?.room || `sportsnew:fixture:${detailFixtureIdRef.current}`, sequence, emittedAt);
      measureWsPatchToRender('fixture', sequence);
      return;
    }
    if (
      !authoritativeEmpty && fixtureSequenceRef.current !== null &&
      sequence !== fixtureSequenceRef.current + 1
    ) {
      const fxId = detailFixtureIdRef.current;
      if (fxId) {
        recoverFixtureDetailSingleFlight(fxId, {
          since: fixtureSequenceRef.current,
          snapshot: true,
          requestRevision: realtimeRevisionBeforePatch,
        });
      }
    }

    fixtureSequenceRef.current = sequence;
    const fixtureMetadataDelta = sportsnewFixtureMetadataDelta(normalizedPatch);
    setDetailFixture((prev) => applyFixturePatchToFixture(prev, fixtureMetadataDelta));
    commitFixtures((prev) => (
      fixtureMembershipTransition
        ? failCloseQuarantinedFixtures(prev)
        : operatorVisibility.action === 'resume'
          ? failCloseQuarantinedFixtures(prev)
          : failCloseQuarantinedFixtures(
            applyFixturePatchToFixtureList(prev, normalizedPatch, detailFixtureIdRef.current),
          )
    ));

    // ── Sync detailMarkets from the patch ──────────────────────────────
    // patch.markets[] and patch.odds[] are INDEPENDENT concerns that can BOTH
    // be present, non-empty, in the same payload (confirmed in real captured
    // live traffic) — applyFixtureDetailPatch processes both unconditionally
    // (never if/else-if between them), returning the computed result as pure
    // data. This handler's job is just to dispatch the resulting side effects.
    if (Array.isArray(normalizedPatch?.markets) || Array.isArray(normalizedPatch?.odds) || normalizedPatch?.oddStateId != null) {
      const { markets: nextMarkets, flashEvents, needsRecovery } =
        applyFixtureDetailPatch(detailMarketsRef.current, normalizedPatch);

      flashEvents.forEach(({ oddStateId, direction }) => flashOdds(oddStateId, direction));
      // Socket callbacks can run again before React commits/effects update the
      // ref. Advance the canonical ref synchronously so consecutive lock
      // patches are applied to one serialized snapshot instead of both
      // branching from the same stale render.
      commitDetailMarkets(nextMarkets);

      if (needsRecovery && detailFixtureIdRef.current) {
        recoverFixtureDetailSingleFlight(detailFixtureIdRef.current, {
          snapshot: true,
          requestRevision: realtimeRevisionBeforePatch,
        });
      }
    }
    if (failClosedFixtureHold) {
      const failClosedFixtureId = patchFixtureId ?? detailFixtureIdRef.current;
      commitDetailMarkets((prev) => failCloseSportsnewDetailMarkets(prev));
      updateDetailCacheForFixtures(
        [failClosedFixtureId],
        (markets) => failCloseSportsnewDetailMarkets(markets),
      );
    }
    // ops patches: fixture metadata only (score, status, etc.) — detailMarkets
    // was already populated by loadFixtureDetail and stays intact.

    sendAck(normalizedPatch?.room || `sportsnew:fixture:${detailFixtureIdRef.current}`, sequence, emittedAt);
    measureWsPatchToRender('fixture', sequence);
  // mode deliberately excluded — read via modeRef above so switching Live/
  // Prematch tabs never recreates this callback (see the socket-connection
  // effect, which tears down and rebuilds the whole socket whenever this
  // identity changes — this was the cause of the reported "connection keeps
  // dropping" / detail panel barely updating symptom).
  }, [
    detailFixtureIdRef, detailRealtimeLedgerRef, fixtureSequenceRef, acceptDisplayPatch,
    socketGenerationRef, fixturesRef, modeRef, displayScopeRef, setDetailFixture, commitFixtures, sendAck,
    updateDetailCacheForFixtures, membershipFailClosedFixturesRef, quarantineMembershipFixtures,
    armMembershipQuarantineReopen, scheduleMembershipGenerationRefresh,
    updateOperatorSuspendedFixtureIds, clearActiveDetailIfSuspended,
    recoverFixtureDetailSingleFlight, failCloseQuarantinedFixtures, detailMarketsRef,
    commitDetailMarkets, flashOdds,
    cancelPendingDetailWork, detailCacheRef, setDetailLoading,
  ]);

  const { handleSelectedPatch, recoverSelectedOddsForCart } = useSportsnewSelectedRealtime({
    SportsnewService,
    frameDeliveryRef,
    acceptDisplayPatch,
    commitDetailMarkets,
    commitFixtures,
    detailCacheRef,
    detailRealtimeLedgerRef,
    displayScope,
    failCloseCartSelectionsForFixtures,
    failCloseQuarantinedFixtures,
    flashOdds,
    isSelectionInAnyCart,
    membershipFailClosedFixturesRef,
    selectedAuthoritativeRecoveryOrdinalRef,
    selectedFreshnessRef: selectedWsFreshnessRef,
    selectedRecoveryInFlightRef,
    selectedSequenceRef,
    selectionsRef,
    sendAck,
    socketGenerationRef,
    socketRef,
    updateOddsInCart,
  });





  const selectedDelivery = useSelectedDelivery({
    sequenceRef: selectedSequenceRef,
    getIds: () => normalizeSelectedRecoveryIds(selectionsRef.current),
    getOwner: () => ({ socket: socketRef.current, session: sessionOwnerRef.current,
      generation: socketGenerationRef.current, mode: modeRef.current, scope: displayScopeRef.current,
      selectionKey: normalizeSelectedRecoveryIds(selectionsRef.current).join(','),
      room: `sportsnew:selected:${socketRef.current?.id || ''}` }),
    apply: handleSelectedPatch,
    recover: options => recoverSelectedOddsForCart(selectionsRef.current, displayScopeRef.current, options),
    acknowledge: ({ patch, sequence, emittedAt }) => sendAck(patch?.room || 'sportsnew:selected', sequence, emittedAt),
  });
  const { frame: frameDelivery, observer: projectionObserver } = useRealtimeDelivery({
    enabled: process.env.REACT_APP_SPORTSNEW_FRAME_BATCHING === 'true', observe: process.env.REACT_APP_SPORTSNEW_TRUSTED_RENDER === 'true', build: process.env.REACT_APP_REVISION,
    ready: listReady && socketConnected, frameRef: frameDeliveryRef, observerRef: projectionObserverRef,
    getRows: () => fixturesRef.current,
    getOwner: lane => ({ socket: socketRef.current, session: sessionOwnerRef.current,
      generation: socketGenerationRef.current, mode: modeRef.current, scope: displayScopeRef.current,
      filter: lane === 'list' ? listFilterKeyRef.current : null,
      room: lane === 'list' && listSubscriptionRef.current
        ? `sportsnew:list:${listSubscriptionRef.current.mode}:${listSubscriptionRef.current.filterHash}`
        : lane === 'fixture' ? sportsnewFixtureRoomForIdentity({ fixtureId: detailFixtureIdRef.current,
          mode: modeRef.current, scope: displayScopeRef.current }) : null,
      detail: lane === 'fixture' ? deliveryDetailGenerationRef.current : null,
      fixture: lane === 'fixture' ? detailFixtureIdRef.current : null }),
    getSequence: lane => lane === 'list' ? listSequenceRef.current : lane === 'fixture' ? fixtureSequenceRef.current : null,
    apply: (lane, envelope) => ({ list: handleListPatch, fixture: handleFixturePatch,
      selected: selectedDelivery.receive, membership: onMembershipInvalidation })[lane]?.(envelope),
    recover: lanes => {
      if (lanes.includes('list')) scheduleMembershipGenerationRefresh();
      if (lanes.includes('fixture') && detailFixtureIdRef.current) {
        return recoverFixtureDetailSingleFlight(detailFixtureIdRef.current, { snapshot: true });
      }
    },
  });

  const handleBetResult = useCallback((data) => {
    // Dispatch a window event so FigmaBetCart / other components can react
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sportsnew:bet:result', { detail: data }));
    }
  }, []);

  const interruptListRequestForSocketRef = useRef(interruptListRequestForSocket);
  const resumeInterruptedListRequestRef = useRef(resumeInterruptedListRequest);
  const handleListPatchRef = useRef(handleListPatch);
  const handleFixturePatchRef = useRef(handleFixturePatch);
  const handleSelectedPatchRef = useRef(handleSelectedPatch);
  const handleBetResultRef = useRef(handleBetResult);
  const handleMembershipInvalidationRef = useRef(onMembershipInvalidation);
  const recoverFixtureDetailSingleFlightRef = useRef(recoverFixtureDetailSingleFlight);

  useEffect(() => {
    interruptListRequestForSocketRef.current = interruptListRequestForSocket;
    resumeInterruptedListRequestRef.current = resumeInterruptedListRequest;
    handleListPatchRef.current = handleListPatch;
    handleFixturePatchRef.current = handleFixturePatch;
    handleSelectedPatchRef.current = handleSelectedPatch;
    handleBetResultRef.current = handleBetResult;
    handleMembershipInvalidationRef.current = onMembershipInvalidation;
    recoverFixtureDetailSingleFlightRef.current = recoverFixtureDetailSingleFlight;
  }, [
    handleBetResult, handleFixturePatch, handleListPatch, handleSelectedPatch,
    interruptListRequestForSocket, resumeInterruptedListRequest,
    onMembershipInvalidation, recoverFixtureDetailSingleFlight,
  ]);

  const sportsnewSocketAuthToken = sessionOwner.authToken;
  const shouldConnectSportsnewSocket = Boolean(SportsnewService && sessionOwner.authenticated);

  /* ─────────────────────────────────────────────────────
     SOCKET CONNECTION
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!shouldConnectSportsnewSocket) return undefined;
    const backUrl = getSportsnewBackUrl();
    if (!backUrl) return undefined;

    const token = sportsnewSocketAuthToken;

    const socketOptions = {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      // Reconnect indefinitely with capped exponential backoff. A finite
      // reconnectionAttempts (previously 10, exhausted in under a minute)
      // meant any network blip longer than that permanently killed live
      // updates for the rest of the session — the socket gave up retrying
      // and nothing short of a full page reload brought it back, matching
      // reports of flash/detail-panel updates "just stopping" over time.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
    };

    if (token) {
      socketOptions.auth = { token };
      socketOptions.extraHeaders = { authorization: `Bearer ${token}` };
    }

    const socket = io(`${backUrl}/ws/sports`, socketOptions);
    let socketHasConnectedOnce = false;
    let socketConnectionActive = false;
    let connectionDisposed = false;

    socket.on('connect', () => {
      if (connectionDisposed || socketConnectionActive
          || socketRef.current !== socket || sessionOwnerRef.current !== sessionOwner) return;
      socketConnectionActive = true;
      setSocketConnected(true);
      const isReconnect = socketHasConnectedOnce;
      if (isReconnect) interruptListRequestForSocketRef.current?.();
      if (isReconnect) socketGenerationRef.current += 1;
      socketHasConnectedOnce = true;
      listSequenceRef.current = null;
      fixtureSequenceRef.current = null;
      selectedSequenceRef.current = null;
      if (isReconnect) {
        handleMembershipInvalidationRef.current?.({
          mode: modeRef.current,
          reason: 'SOCKET_RECONNECTED',
        });
      }

      // Re-subscribe to list room
      const sub = listSubscriptionRef.current;
      if (sub) {
        subscribeListWithGenerationFence(socket, sub, {
          isReconnect,
          requiredRequestSequence: fixturesRequestSeqRef.current + 1,
        });
      }

      resumeInterruptedListRequestRef.current?.();

      // Re-subscribe to fixture room (use refs — this handler is created once
      // when the socket is opened and never recreated on mode/fixture changes,
      // so both must be read live rather than closed over).
      const fixtureSubscription = fixtureSubscriptionRef.current;
      if (fixtureSubscription) {
        socket.emit('subscribe:sportsnew:fixture', fixtureSubscription);
        if (isReconnect) {
          recoverFixtureDetailSingleFlightRef.current(fixtureSubscription.fixtureId, { snapshot: true });
        }
      }

      // Resume only current recovery intent after reconnect; never replay an old result.
      for (const [key, flight] of fixtureRecoveryInFlightRef.current) {
        if (!flight.isIntentCurrent?.()) {
          flight.controller?.abort();
          fixtureRecoveryInFlightRef.current.delete(key);
          continue;
        }
        if (!flight.pending && flight.owner.socketGeneration === socketGenerationRef.current) continue;
        const remaining = [...flight.focus].filter(id => !flight.served.has(id));
        if (remaining.length) recoverFixtureDetailSingleFlightRef.current(flight.fixtureId,
          { snapshot: true, focusOddStateIds: remaining });
      }

      // Re-subscribe the exact current cart, not the hook creation snapshot.
      const selectedSubscription = selectedSubscriptionRef.current;
      if (selectedSubscription) {
        selectedDelivery.subscribe(socket, selectedSubscription);
      }
    });

    socket.on('disconnect', () => {
      if (connectionDisposed || socketRef.current !== socket
          || sessionOwnerRef.current !== sessionOwner) return;
      socketConnectionActive = false;
      frameDelivery.cancel();
      interruptListRequestForSocketRef.current?.();
      socketGenerationRef.current += 1;
      listHeartbeatFenceRef.current?.cancel?.();
      listHeartbeatFenceRef.current = null;
      listSubscriptionRecoveryRef.current.cancel();
      setSocketConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.warn('[sportsnew] socket connect error:', err.message);
    });

    const rawListPatch = (raw) => {
      if (sessionOwnerRef.current !== sessionOwner || socketRef.current !== socket || !socket.connected) return;
      if (SPORTSNEW_WS_DEBUG) console.log('[sportsnew:raw] sportsnew:list:patch', raw);
      frameDelivery.receive('list', raw);
    };
    const rawFixturePatch = (raw) => {
      if (sessionOwnerRef.current !== sessionOwner || socketRef.current !== socket || !socket.connected) return;
      if (isExplicitEmptyFullMarketProjection(raw?.patch)
        && (sessionOwnerRef.current !== sessionOwner || socketRef.current !== socket)) return;
      if (SPORTSNEW_WS_DEBUG) console.log('[sportsnew:raw] sportsnew:fixture:patch', raw);
      frameDelivery.receive('fixture', raw);
    };
    const rawSelectedPatch = (raw) => {
      if (sessionOwnerRef.current !== sessionOwner || socketRef.current !== socket || !socket.connected) return;
      if (SPORTSNEW_WS_DEBUG) console.log('[sportsnew:raw] sportsnew:selected:patch', raw);
      frameDelivery.receive('selected', raw);
    };
    const rawBetResult = (raw) => handleBetResultRef.current(raw);
    const rawMembershipInvalidation = (raw) => {
      if (sessionOwnerRef.current !== sessionOwner || socketRef.current !== socket || !socket.connected) return;
      frameDelivery.receive('membership', raw);
    };

    socket.on('sportsnew:list:patch', rawListPatch);
    socket.on('sportsnew:fixture:patch', rawFixturePatch);
    socket.on('sportsnew:selected:patch', rawSelectedPatch);
    socket.on('sportsnew:bet:result', rawBetResult);
    socket.on(SPORTSNEW_MEMBERSHIP_INVALIDATE_EVENT, rawMembershipInvalidation);

    socketRef.current = socket;

    return () => {
      connectionDisposed = true;
      frameDelivery.cancel();
      socket.off('sportsnew:list:patch', rawListPatch);
      socket.off('sportsnew:fixture:patch', rawFixturePatch);
      socket.off('sportsnew:selected:patch', rawSelectedPatch);
      socket.off('sportsnew:bet:result', rawBetResult);
      socket.off(SPORTSNEW_MEMBERSHIP_INVALIDATE_EVENT, rawMembershipInvalidation);
      socketGenerationRef.current += 1;
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  }, [
    detailFixtureIdRef, displayScopeRef, fixtureRecoveryInFlightRef, fixtureSequenceRef,
    fixturesRequestSeqRef, listHeartbeatFenceRef, listSequenceRef, listSubscriptionRecoveryRef,
    listSubscriptionRef, modeRef, selectedSequenceRef, selectedSubscriptionRef, setSocketConnected,
    shouldConnectSportsnewSocket, socketGenerationRef, socketRef, sportsnewSocketAuthToken, sessionOwner, sessionOwnerRef,
    subscribeListWithGenerationFence, frameDelivery, selectedDelivery.subscribe,
  ]);


  /* ─────────────────────────────────────────────────────
     LIST-ROOM HEARTBEAT (send while list page is active)
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!socketConnected) return undefined;

    heartbeatRef.current = setInterval(() => {
      const currentSubscription = listSubscriptionRef.current;
      const socket = socketRef.current;
      if (currentSubscription && socket?.connected) {
        heartbeatListWithGenerationFence(socket, currentSubscription);
      }
    }, 25000);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [heartbeatListWithGenerationFence, heartbeatRef, listSubscriptionRef, socketConnected, socketRef]);

  /* ─────────────────────────────────────────────────────
     BETSLIP — subscribe selected odds whenever cart changes
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    const oddStateIds = normalizeSelectedRecoveryIds(selections);
    const selectedSubscription = oddStateIds.length > 0
      ? { oddStateIds, scope: displayScope }
      : null;
    selectedSubscriptionRef.current = selectedSubscription;
    const activeIds = new Set(oddStateIds);
    selectedWsFreshnessRef.current.forEach((value, oddStateId) => {
      if (!activeIds.has(oddStateId)) selectedWsFreshnessRef.current.delete(oddStateId);
    });

    if (!socketConnected) return undefined;

    if (oddStateIds.length === 0) {
      selectedDelivery.retire();
      socketRef.current?.emit('unsubscribe:sportsnew:selected', {});
      return undefined;
    }

    if (socketRef.current) selectedDelivery.subscribe(socketRef.current, selectedSubscription);

    return undefined;
  }, [displayScope, selectedSubscriptionRef, selectedWsFreshnessRef, selections, socketConnected, socketRef, selectedDelivery.subscribe, selectedDelivery.retire]);

  useEffect(() => {
    if (!socketConnected) return undefined;
    selectedHeartbeatRef.current = setInterval(() => {
      const currentSubscription = selectedSubscriptionRef.current;
      if (currentSubscription) {
        selectedDelivery.heartbeat(socketRef.current, currentSubscription);
      }
    }, 25000);
    return () => {
      if (selectedHeartbeatRef.current) {
        clearInterval(selectedHeartbeatRef.current);
        selectedHeartbeatRef.current = null;
      }
    };
  }, [selectedSubscriptionRef, socketConnected, socketRef]);

  /* ─────────────────────────────────────────────────────
     FIXTURE DETAIL — subscribe / unsubscribe on navigate
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    const fixtureMode = mode === 'all' ? 'prematch' : mode;
    const subscription = detailFixtureId
      ? { fixtureId: detailFixtureId, mode: fixtureMode, scope: displayScope }
      : null;
    fixtureSubscriptionRef.current = subscription;
    if (!socketConnected || !subscription) return undefined;
    socketRef.current?.emit('subscribe:sportsnew:fixture', subscription);

    return () => {
      socketRef.current?.emit('unsubscribe:sportsnew:fixture', subscription);
      fixtureSequenceRef.current = null;
    };
  }, [socketConnected, detailFixtureId, displayScope, fixtureSequenceRef, mode, socketRef]);

  useEffect(() => {
    if (!socketConnected) return undefined;
    fixtureHeartbeatRef.current = setInterval(() => {
      const currentSubscription = fixtureSubscriptionRef.current;
      if (currentSubscription) {
        socketRef.current?.emit('heartbeat:sportsnew:fixture', currentSubscription, () => {});
      }
    }, 25000);
    return () => {
      if (fixtureHeartbeatRef.current) {
        clearInterval(fixtureHeartbeatRef.current);
        fixtureHeartbeatRef.current = null;
      }
    };
  }, [socketConnected, socketRef]);

  /* ─────────────────────────────────────────────────────
     API — initial load: categories + settings
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!SportsnewService) return;

    Promise.allSettled([
      SportsnewService.getCategories(),
      SportsnewService.getDisplaySettings(),
      SportsnewService.getComboBonus(),
    ]).then(([catRes, settingsRes, bonusRes]) => {
      if (catRes.status === 'fulfilled' && catRes.value?.data) {
        setCategories(normalizeSportsnewCategories(catRes.value.data));
      }
      if (settingsRes.status === 'fulfilled' && settingsRes.value?.data) {
        setDisplaySettings(settingsRes.value.data);
      }
      if (bonusRes.status === 'fulfilled' && bonusRes.value?.data) {
        setComboBonus(normalizeSportsnewComboBonus(bonusRes.value.data));
        if (bonusRes.value?.buckets) setComboBuckets(bonusRes.value.buckets);
      }
    });
  }, [SportsnewService, setCategories, setComboBonus, setComboBuckets, setDisplaySettings]);


  return { flashOdds, recoverSelectedOddsForCart, projectionObserver, selectedDelivery };
};
